import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import { Button, toast } from '@feathr/components';

export type TMappingDisabledReason = 'required' | 'provisioning';

export interface IDeleteMappingButtonProps {
  disabled?: boolean;
  /**
   * Whether or not to provide a standardized tooltip based on the reason for the button being disabled.
   * If provided, the tooltip prop will be overridden.
   */
  disabledReason?: TMappingDisabledReason;
  id: string;
  integration: BlackbaudRaisersEdgeIntegration;
  tooltip?: string;
}

function DeleteMappingButton({
  disabled = false,
  disabledReason,
  id,
  integration,
  tooltip,
}: Readonly<IDeleteMappingButtonProps>): JSX.Element {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  async function deleteMapping(): Promise<void> {
    if (isDeleting) {
      return;
    }
    setIsDeleting(true);
    try {
      await integration.contactMappingStore.remove(id);
      toast(t('Deleted mapping.'), {
        type: 'success',
      });
    } catch (error) {
      // The server may have deleted the mapping before the request failed.
      toast(t("Couldn't delete mapping. Check the list and try again."), {
        type: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  }

  const getTooltipMessage = (disabledReason: TMappingDisabledReason): string | null => {
    const map: Record<TMappingDisabledReason, string> = {
      required: t('This contact mapping cannot be deleted because it is required.'),
      provisioning: t('This contact mapping cannot be deleted because it is being provisioned.'),
    };

    return map[disabledReason];
  };

  return (
    <Button
      aria-label={t('Delete mapping')}
      disabled={disabled || isDeleting}
      name={'delete-contact-mapping'}
      onClick={deleteMapping}
      tooltip={disabled && disabledReason ? getTooltipMessage(disabledReason) : tooltip}
      tooltipPosition={'top-end'}
      type={'icon'}
    >
      <FontAwesomeIcon icon={faTrash} />
    </Button>
  );
}

export default observer(DeleteMappingButton);
